/**
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 */
import type { Matrix4, OrthographicCamera, PerspectiveCamera, BaseEvent } from "three";
import { MathUtils, MOUSE, Quaternion, Spherical, Vector2, Vector3 } from "three";
import { CanvasControls } from "./CanvasControls";
import { htmlElementIsGUI } from "./htmlElementIsGUI";

const CHANGE_EVENT: BaseEvent<"change"> = { type: "change" };
const PROJECTION_CHANGE_EVENT: BaseEvent<"projectionChange"> = { type: "projectionChange" };
const START_EVENT: BaseEvent<"start"> = { type: "start" };
const END_EVENT: BaseEvent<"end"> = { type: "end" };
const STATE = { NONE: -1, ROTATE: 0, DOLLY: 1, PAN: 2, TOUCH_ROTATE: 3, TOUCH_DOLLY_PAN: 4 };
const EPS = 0.000001;

/**
 * This set of controls performs orbiting, dollying (zooming), and panning.
 * Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
 *
 * - Orbit - left mouse / touch: one-finger move
 * - Zoom - middle mouse, or mousewheel / touch: two-finger spread or squish
 * - Pan - right mouse, left mouse + ctrl/meta/shiftKey / touch: two-finger move
 */
export default class PerspectiveControls extends CanvasControls {
  constructor(
    object: OrthographicCamera | PerspectiveCamera,
    domElement: HTMLElement | undefined | null,
    targetX: number,
    targetY: number,
    targetZ: number,
    ..._moreArgs: unknown[]
  ) {
    super(domElement ?? document, object, new Vector3(targetX, targetY, targetZ));
    this.object = object;
    this.lookAtControls = false;
    this.minViewableWidth = 0;
    this.maxPolarAngle = 1.57;
    this.target0 = this.target.clone();
    this.position0 = this.object.position.clone();
    this.zoom0 = this.object.zoom;
    this.#baselineRadius = this.position0.distanceTo(this.target0);

    // Private state (update IIFE closure)
    this.#updateOffset = new Vector3();
    this.#updateQuat = new Quaternion().setFromUnitVectors(object.up, new Vector3(0, 1, 0));
    this.#updateQuatInverse = this.#updateQuat.clone().invert();
    this.#lastPosition = new Vector3();
    this.#lastQuaternion = new Quaternion();

    this.#panLeftV = new Vector3();
    this.#panUpV = new Vector3();
    this.#panOffset = new Vector3();

    this.addDomElementListeners();
    this.update();
  }

  lookAtControls: boolean;
  minViewableWidth: number;
  target0: Vector3;
  position0: Vector3;
  zoom0: number;

  /** Narrow to HTMLElement so this class satisfies THREEControls (which expects domElement: HTMLElement). */
  override get domElement(): HTMLElement {
    return this._domElement as HTMLElement;
  }

  override set domElement(value: HTMLElement | Document | undefined | null) {
    super.domElement = value;
  }

  #state = STATE.NONE;
  #spherical = new Spherical();
  #sphericalDelta = new Spherical();
  #scale = 1;
  #panOffset = new Vector3();
  #zoomChanged = false;
  #rotateStart = new Vector2();
  #rotateEnd = new Vector2();
  #rotateDelta = new Vector2();
  #panStart = new Vector2();
  #panEnd = new Vector2();
  #panDelta = new Vector2();
  #dollyStart = new Vector2();
  #dollyEnd = new Vector2();
  #dollyDelta = new Vector2();
  #updateOffset = new Vector3();
  #updateQuat = new Quaternion();
  #updateQuatInverse = new Quaternion();
  #lastPosition = new Vector3();
  #lastQuaternion = new Quaternion();
  #panLeftV = new Vector3();
  #panUpV = new Vector3();
  #panTempOffset = new Vector3();
  #baselineRadius = 0;

  #onContextMenuListener = (event: MouseEvent) => this.#onContextMenu(event);
  #onMouseDownListener = (event: MouseEvent) => this.#onMouseDown(event);
  #onMouseMoveListener = (event: MouseEvent) => this.#onMouseMove(event);
  #onMouseUpListener = () => this.#onMouseUp();
  #onMouseWheelListener = (event: WheelEvent) => this.#onMouseWheel(event);
  #onTouchStartListener = (event: TouchEvent) => this.#onTouchStart(event);
  #onTouchEndListener = () => this.#onTouchEnd();
  #onTouchMoveListener = (event: TouchEvent) => this.#onTouchMove(event);

  zoomTowardsCentre(): void {
    this.#dollyIn(this.#getZoomScale());
    this.update();
  }

  zoomOutFromCentre(): void {
    this.#dollyOut(this.#getZoomScale());
    this.update();
  }

  getPolarAngle(): number {
    return this.#spherical.phi;
  }

  getAzimuthalAngle(): number {
    return this.#spherical.theta;
  }

  setPolarAngle(value: number): void {
    this.#rotateUp(-(value - this.#spherical.phi));
  }

  setAzimuthalAngle(value: number): void {
    this.#rotateLeft(-(value - this.#spherical.theta));
  }

  saveState(): void {
    this.target0.copy(this.target);
    this.position0.copy(this.object.position);
    this.zoom0 = this.object.zoom;
    this.#baselineRadius = this.position0.distanceTo(this.target0);
  }

  reset(): void {
    this.target.copy(this.target0);
    this.object.position.copy(this.position0);
    this.object.zoom = this.zoom0;
    this.object.updateProjectionMatrix();
    this.dispatchEvent(PROJECTION_CHANGE_EVENT);
    this.dispatchEvent(CHANGE_EVENT);
    this.update();
    this.#state = STATE.NONE;
  }

  update(): boolean {
    const position = this.object.position;
    if (!this.lookAtControls) {
      this.#updateOffset.copy(position).sub(this.target);
      this.#updateOffset.applyQuaternion(this.#updateQuat);
      this.#spherical.setFromVector3(this.#updateOffset);
    }
    if (this.autoRotate && this.#state === STATE.NONE) {
      this.#rotateLeft(this.getAutoRotationAngle());
    }
    this.#spherical.theta += this.#sphericalDelta.theta;
    this.#spherical.phi += this.#sphericalDelta.phi;
    this.#spherical.theta = Math.max(this.minAzimuthAngle, Math.min(this.maxAzimuthAngle, this.#spherical.theta));
    this.#spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.#spherical.phi));
    this.#spherical.makeSafe();
    this.#spherical.radius *= this.#scale;
    const minMaxDistance = this.getMinMaxDistance();
    this.#spherical.radius = Math.max(
      this.minDistance,
      Math.min(Math.max(this.maxDistance, minMaxDistance), this.#spherical.radius),
    );
    this.target.add(this.#panOffset);
    if (!this.lookAtControls) {
      this.#updateOffset.setFromSpherical(this.#spherical);
      this.#updateOffset.applyQuaternion(this.#updateQuatInverse);
      position.copy(this.target).add(this.#updateOffset);
    }
    this.object.lookAt(this.target);
    if (this.enableDamping === true) {
      this.#sphericalDelta.theta *= 1 - this.dampingFactor;
      this.#sphericalDelta.phi *= 1 - this.dampingFactor;
      this.#panOffset.multiplyScalar(1 - this.dampingFactor);
    } else {
      this.#sphericalDelta.set(0, 0, 0);
      this.#panOffset.set(0, 0, 0);
    }
    this.#scale = 1;
    if (
      this.#zoomChanged ||
      this.#lastPosition.distanceToSquared(this.object.position) > EPS ||
      8 * (1 - this.#lastQuaternion.dot(this.object.quaternion)) > EPS
    ) {
      this.dispatchEvent(CHANGE_EVENT);
      this.#lastPosition.copy(this.object.position);
      this.#lastQuaternion.copy(this.object.quaternion);
      this.#zoomChanged = false;
      return true;
    }
    return false;
  }

  isAtZoomLimit(): { in: boolean; out: boolean } {
    const radius = this.#spherical.radius;
    const effectiveMax = Math.max(this.maxDistance, this.getMinMaxDistance());
    const eps = 1e-3;
    return {
      in: radius <= this.minDistance + eps,
      out: radius >= effectiveMax - eps,
    };
  }

  getZoomFraction(): number {
    const radius = this.#spherical.radius || this.object.position.distanceTo(this.target);
    if (radius <= 0 || this.#baselineRadius <= 0) {
      return 1;
    }
    return this.#baselineRadius / radius;
  }

  override getMinMaxDistance(): number {
    if (this.minViewableWidth > 0) {
      const cam = this.object as PerspectiveCamera;
      if (cam.fov > 0 && cam.aspect > 0) {
        const verticalFov = MathUtils.DEG2RAD * cam.fov;
        const tanHalfHorizontalFov = Math.tan(verticalFov * 0.5) * cam.aspect;
        return (this.minViewableWidth * 0.5) / tanHalfHorizontalFov;
      }
    }
    return 0;
  }

  addDomElementListeners(): void {
    this.domElement.addEventListener("contextmenu", this.#onContextMenuListener, false);
    this.domElement.addEventListener("mousedown", this.#onMouseDownListener, false);
    this.domElement.addEventListener("wheel", this.#onMouseWheelListener, false);
    this.domElement.addEventListener("touchstart", this.#onTouchStartListener, false);
    this.domElement.addEventListener("touchend", this.#onTouchEndListener, false);
    this.domElement.addEventListener("touchmove", this.#onTouchMoveListener, false);
  }

  removeDomElementListeners(): void {
    this.domElement.removeEventListener("contextmenu", this.#onContextMenuListener, false);
    this.domElement.removeEventListener("mousedown", this.#onMouseDownListener, false);
    this.domElement.removeEventListener("wheel", this.#onMouseWheelListener, false);
    this.domElement.removeEventListener("touchstart", this.#onTouchStartListener, false);
    this.domElement.removeEventListener("touchend", this.#onTouchEndListener, false);
    this.domElement.removeEventListener("touchmove", this.#onTouchMoveListener, false);
  }

  dispose(): void {
    this.removeDomElementListeners();
    document.removeEventListener("mousemove", this.#onMouseMoveListener, false);
    document.removeEventListener("mouseup", this.#onMouseUpListener, false);
  }

  #getZoomScale(): number {
    return Math.pow(0.95, this.zoomSpeed);
  }

  #rotateLeft(angle: number): void {
    this.#sphericalDelta.theta -= angle;
  }

  #rotateUp(angle: number): void {
    this.#sphericalDelta.phi -= angle;
  }

  #panLeft(distance: number, objectMatrix: Matrix4): void {
    this.#panLeftV.setFromMatrixColumn(objectMatrix, 0);
    this.#panLeftV.multiplyScalar(-distance);
    this.#panOffset.add(this.#panLeftV);
  }

  #panUp(distance: number, objectMatrix: Matrix4): void {
    if (this.screenSpacePanning === true) {
      this.#panUpV.setFromMatrixColumn(objectMatrix, 1);
    } else {
      this.#panUpV.setFromMatrixColumn(objectMatrix, 0);
      this.#panUpV.crossVectors(this.object.up, this.#panUpV);
    }
    this.#panUpV.multiplyScalar(distance);
    this.#panOffset.add(this.#panUpV);
  }

  #pan(deltaX: number, deltaY: number): void {
    const element: HTMLElement =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
    const cam = this.object as { isPerspectiveCamera?: boolean; fov?: number; matrix: Matrix4; position: Vector3 };
    if (cam.isPerspectiveCamera) {
      const position = this.object.position;
      this.#panTempOffset.copy(position).sub(this.target);
      let targetDistance = this.#panTempOffset.length();
      targetDistance *= Math.tan(((cam.fov ?? 0) / 2) * (Math.PI / 180.0));
      this.#panLeft((2 * deltaX * targetDistance) / element.clientHeight, this.object.matrix);
      this.#panUp((2 * deltaY * targetDistance) / element.clientHeight, this.object.matrix);
    } else {
      console.warn("WARNING: PerspectiveControls.js encountered an unknown camera type - pan disabled.");
      this.enablePan = false;
    }
  }

  #dollyOut(dollyScale: number): void {
    if ((this.object as { isPerspectiveCamera?: boolean }).isPerspectiveCamera) {
      this.#scale /= dollyScale;
    } else {
      console.warn("WARNING: PerspectiveControls.js encountered an unknown camera type - dolly/zoom disabled.");
      this.enableZoom = false;
    }
  }

  #dollyIn(dollyScale: number): void {
    if ((this.object as { isPerspectiveCamera?: boolean }).isPerspectiveCamera) {
      this.#scale *= dollyScale;
    } else {
      console.warn("WARNING: PerspectiveControls.js encountered an unknown camera type - dolly/zoom disabled.");
      this.enableZoom = false;
    }
  }

  #handleMouseDownRotate(event: MouseEvent): void {
    this.#rotateStart.set(event.clientX, event.clientY);
  }

  #handleMouseDownPan(event: MouseEvent): void {
    this.#panStart.set(event.clientX, event.clientY);
  }

  #handleMouseDownDolly(event: MouseEvent): void {
    this.#dollyStart.set(event.clientX, event.clientY);
  }

  #handleMouseMoveRotate(event: MouseEvent): void {
    this.#rotateEnd.set(event.clientX, event.clientY);
    this.#rotateDelta.subVectors(this.#rotateEnd, this.#rotateStart).multiplyScalar(this.rotateSpeed);
    const element: HTMLElement =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
    this.#rotateLeft((2 * Math.PI * this.#rotateDelta.x) / element.clientHeight);
    this.#rotateUp((2 * Math.PI * this.#rotateDelta.y) / element.clientHeight);
    this.#rotateStart.copy(this.#rotateEnd);
    this.update();
  }

  #handleMouseMoveDolly(event: MouseEvent): void {
    this.#dollyEnd.set(event.clientX, event.clientY);
    this.#dollyDelta.subVectors(this.#dollyEnd, this.#dollyStart);
    if (this.#dollyDelta.y > 0) {
      this.#dollyOut(this.#getZoomScale());
    } else if (this.#dollyDelta.y < 0) {
      this.#dollyIn(this.#getZoomScale());
    }
    this.#dollyStart.copy(this.#dollyEnd);
    this.update();
  }

  #handleMouseMovePan(event: MouseEvent): void {
    this.#panEnd.set(event.clientX, event.clientY);
    this.#panDelta.subVectors(this.#panEnd, this.#panStart).multiplyScalar(this.panSpeed);
    this.#pan(this.#panDelta.x, this.#panDelta.y);
    this.#panStart.copy(this.#panEnd);
    this.update();
  }

  #handleMouseWheel(event: WheelEvent): void {
    if (event.deltaY < 0) {
      this.#dollyIn(this.#getZoomScale());
    } else if (event.deltaY > 0) {
      this.#dollyOut(this.#getZoomScale());
    }
    this.update();
  }

  #handleTouchStartRotate(event: TouchEvent): void {
    this.#rotateStart.set(event.touches[0].pageX, event.touches[0].pageY);
  }

  #handleTouchStartDollyPan(event: TouchEvent): void {
    if (this.enableZoom) {
      const dx = event.touches[0].pageX - event.touches[1].pageX;
      const dy = event.touches[0].pageY - event.touches[1].pageY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      this.#dollyStart.set(0, distance);
    }
    if (this.enablePan) {
      const x = 0.5 * (event.touches[0].pageX + event.touches[1].pageX);
      const y = 0.5 * (event.touches[0].pageY + event.touches[1].pageY);
      this.#panStart.set(x, y);
    }
  }

  #handleTouchMoveRotate(event: TouchEvent): void {
    this.#rotateEnd.set(event.touches[0].pageX, event.touches[0].pageY);
    this.#rotateDelta.subVectors(this.#rotateEnd, this.#rotateStart).multiplyScalar(this.rotateSpeed);
    const element: HTMLElement =
      this._domElement === document ? (this._domElement as Document).body : (this._domElement as HTMLElement);
    this.#rotateLeft((2 * Math.PI * this.#rotateDelta.x) / element.clientHeight);
    this.#rotateUp((2 * Math.PI * this.#rotateDelta.y) / element.clientHeight);
    this.#rotateStart.copy(this.#rotateEnd);
    this.update();
  }

  #handleTouchMoveDollyPan(event: TouchEvent): void {
    if (this.enableZoom) {
      const dx = event.touches[0].pageX - event.touches[1].pageX;
      const dy = event.touches[0].pageY - event.touches[1].pageY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      this.#dollyEnd.set(0, distance);
      this.#dollyDelta.set(0, Math.pow(this.#dollyEnd.y / this.#dollyStart.y, this.zoomSpeed));
      this.#dollyOut(this.#dollyDelta.y);
      this.#dollyStart.copy(this.#dollyEnd);
    }
    if (this.enablePan) {
      const x = 0.5 * (event.touches[0].pageX + event.touches[1].pageX);
      const y = 0.5 * (event.touches[0].pageY + event.touches[1].pageY);
      this.#panEnd.set(x, y);
      this.#panDelta.subVectors(this.#panEnd, this.#panStart).multiplyScalar(this.panSpeed);
      this.#pan(this.#panDelta.x, this.#panDelta.y);
      this.#panStart.copy(this.#panEnd);
    }
    this.update();
  }

  #onMouseDown(event: MouseEvent): void {
    if (this.enabled === false) {
      return;
    }
    if (event.button === MOUSE.LEFT && this.ignoreLeftClickWithoutCtrl && !event.ctrlKey) {
      return;
    }
    if (htmlElementIsGUI(event.target)) {
      return;
    }
    event.preventDefault();
    if (typeof (this.domElement as HTMLElement).focus === "function") {
      (this.domElement as HTMLElement).focus();
    } else {
      window.focus();
    }
    switch (event.button) {
      case MOUSE.LEFT:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.enablePan === false) {
            return;
          }
          this.#handleMouseDownPan(event);
          this.#state = STATE.PAN;
        } else {
          if (this.enableRotate === false) {
            return;
          }
          this.#handleMouseDownRotate(event);
          this.#state = STATE.ROTATE;
        }
        break;
      case MOUSE.MIDDLE:
        if (this.enableZoom === false) {
          return;
        }
        this.#handleMouseDownDolly(event);
        this.#state = STATE.DOLLY;
        break;
      case MOUSE.RIGHT:
        if (this.enablePan === false) {
          return;
        }
        this.#handleMouseDownPan(event);
        this.#state = STATE.PAN;
        break;
    }
    if (this.#state !== STATE.NONE) {
      document.addEventListener("mousemove", this.#onMouseMoveListener, false);
      document.addEventListener("mouseup", this.#onMouseUpListener, false);
      this.dispatchEvent(START_EVENT);
    }
  }

  #onMouseMove(event: MouseEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
    switch (this.#state) {
      case STATE.ROTATE:
        if (this.enableRotate === false) {
          return;
        }
        this.#handleMouseMoveRotate(event);
        break;
      case STATE.DOLLY:
        if (this.enableZoom === false) {
          return;
        }
        this.#handleMouseMoveDolly(event);
        break;
      case STATE.PAN:
        if (this.enablePan === false) {
          return;
        }
        this.#handleMouseMovePan(event);
        break;
    }
  }

  #onMouseUp(): void {
    document.removeEventListener("mousemove", this.#onMouseMoveListener, false);
    document.removeEventListener("mouseup", this.#onMouseUpListener, false);
    if (this.#state !== STATE.NONE) {
      this.dispatchEvent(END_EVENT);
      this.#state = STATE.NONE;
    }
  }

  #onMouseWheel(event: WheelEvent): void {
    if (
      this.enabled === false ||
      this.enableZoom === false ||
      (this.#state !== STATE.NONE && this.#state !== STATE.ROTATE)
    ) {
      return;
    }
    if (htmlElementIsGUI(event.target)) {
      return;
    }
    event.preventDefault();
    this.dispatchEvent(START_EVENT);
    this.#handleMouseWheel(event);
    this.dispatchEvent(END_EVENT);
  }

  #onTouchStart(event: TouchEvent): void {
    if (this.enabled === false) {
      return;
    }
    if (htmlElementIsGUI(event.target)) {
      return;
    }
    event.preventDefault();
    switch (event.touches.length) {
      case 1:
        if ((this.object as { isPerspectiveCamera?: boolean }).isPerspectiveCamera) {
          if (this.enableRotate === false) {
            return;
          }
          this.#handleTouchStartRotate(event);
          this.#state = STATE.TOUCH_ROTATE;
        }
        break;
      case 2:
        if (this.enableZoom === false && this.enablePan === false) {
          return;
        }
        this.#handleTouchStartDollyPan(event);
        this.#state = STATE.TOUCH_DOLLY_PAN;
        break;
      default:
        this.#state = STATE.NONE;
    }
    if (this.#state !== STATE.NONE) {
      this.dispatchEvent(START_EVENT);
    }
  }

  #onTouchMove(event: TouchEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
    switch (event.touches.length) {
      case 1:
        if (this.enableRotate === false) {
          return;
        }
        if (this.#state !== STATE.TOUCH_ROTATE) {
          return;
        }
        this.#handleTouchMoveRotate(event);
        break;
      case 2:
        if (this.enableZoom === false && this.enablePan === false) {
          return;
        }
        if (this.#state !== STATE.TOUCH_DOLLY_PAN) {
          return;
        }
        this.#handleTouchMoveDollyPan(event);
        break;
      default:
        this.#state = STATE.NONE;
    }
  }

  #onTouchEnd(): void {
    if (this.enabled === false) {
      return;
    }
    this.dispatchEvent(END_EVENT);
    this.#state = STATE.NONE;
  }

  #onContextMenu(event: MouseEvent): void {
    if (this.enabled === false) {
      return;
    }
    event.preventDefault();
  }
}
